import React, { useMemo } from 'react';
import './marketOverviewCards.css';
import { computeMileageStats, computeTypicalSoldDom, computeDealerTypeSplit, computeActiveDealerTypeSplit } from '../utils/overviewStatsUtils';

export default function MarketOverviewCards({
  targetMileage,
  active_listings = [],
  sold_listings = [],
  selectedPeriod = "90D"
}) {
  // Filter data by time period
  const filterByPeriod = useMemo(() => {
    const now = new Date();
    let daysAgo = 30;
    if (selectedPeriod === "60D") daysAgo = 60;
    else if (selectedPeriod === "90D") daysAgo = 90;
    
    const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
    
    return (dateString) => {
      if (!dateString) return false;
      const date = new Date(dateString);
      return date >= cutoffDate;
    };
  }, [selectedPeriod]);

  // Filter listings by time period
  const filteredActiveListings = useMemo(() => {
    return active_listings.filter(listing => 
      filterByPeriod(listing.first_seen_at_date)
    );
  }, [active_listings, filterByPeriod]);

  const filteredSoldListings = useMemo(() => {
    return sold_listings.filter(sale => 
      filterByPeriod(sale.last_seen_at_date)
    );
  }, [sold_listings, filterByPeriod]);

  // Compute all statistics using filtered data
  const mileageStats = useMemo(() => {
    return computeMileageStats(targetMileage, filteredActiveListings, filteredSoldListings);
  }, [targetMileage, filteredActiveListings, filteredSoldListings]);

  const typicalDomStats = useMemo(() => {
    return computeTypicalSoldDom(targetMileage, filteredSoldListings);
  }, [targetMileage, filteredSoldListings]);

  const dealerTypeStats = useMemo(() => {
    return computeDealerTypeSplit(filteredSoldListings);
  }, [filteredSoldListings]);

  const activeDealerTypeStats = useMemo(() => {
    return computeActiveDealerTypeSplit(filteredActiveListings);
  }, [filteredActiveListings]);

  // Calculate marker position for mileage positioning bar
  const getMarkerPosition = () => {
    if (!mileageStats.min || !mileageStats.max || mileageStats.min === mileageStats.max) {
      return 50; // Center if no range
    }
    const position = ((targetMileage - mileageStats.min) / (mileageStats.max - mileageStats.min)) * 100;
    return Math.max(0, Math.min(100, position)); // Clamp between 0 and 100
  };

  const markerPosition = getMarkerPosition();

  // Format mileage ranges for segments
  const formatMileage = (miles) => {
    if (miles >= 1000) {
      return `${(miles / 1000).toFixed(0)}k`;
    }
    return miles.toString();
  };

  return (
    <div className="market-overview-cards">
      {/* Card 1: Mileage Positioning */}
      <div className="moc-card moc-mileage-positioning">
        <div className="moc-title">Mileage Positioning</div>
        <div className="moc-primary-value">
          {targetMileage.toLocaleString()} miles
        </div>

        {/* Segmented Distribution Bar */}
        {mileageStats.q1 && mileageStats.median && mileageStats.q3 && (
          <div className="moc-distribution-container">
            <div className="moc-distribution-bar">
              {/* Segment 1: < Q1 */}
              <div 
                className="moc-segment moc-segment-1"
                style={{ width: '25%' }}
              ></div>
              {/* Segment 2: Q1 - Median */}
              <div 
                className="moc-segment moc-segment-2"
                style={{ width: '25%' }}
              ></div>
              {/* Segment 3: Median - Q3 */}
              <div 
                className="moc-segment moc-segment-3"
                style={{ width: '25%' }}
              ></div>
              {/* Segment 4: > Q3 */}
              <div 
                className="moc-segment moc-segment-4"
                style={{ width: '25%' }}
              ></div>
              
              {/* Marker */}
              <div 
                className="moc-marker"
                style={{ left: `${markerPosition}%` }}
              >
                <div className="moc-marker-pin">📍</div>
              </div>
            </div>
            
            {/* Labels */}
            <div className="moc-distribution-labels">
              <div className="moc-label">&lt;{formatMileage(mileageStats.q1)}</div>
              <div className="moc-label">{formatMileage(mileageStats.q1)}-{formatMileage(mileageStats.median)}</div>
              <div className="moc-label">{formatMileage(mileageStats.median)}-{formatMileage(mileageStats.q3)}</div>
              <div className="moc-label">{formatMileage(mileageStats.q3)}+</div>
            </div>
          </div>
        )}

        <div className="moc-mileage-spacer"></div>

        {mileageStats.percentileHigher !== null && (
          <div className="moc-footer-text">
            {mileageStats.isHigherMileage ? (
              <>Higher mileage than ~{mileageStats.percentLower}% of vehicles in this market</>
            ) : (
              <>Lower mileage than ~{mileageStats.percentHigher}% of vehicles in this market</>
            )}
          </div>
        )}
      </div>

      {/* Card 2: Typical Sold DOM */}
      <div className="moc-card moc-typical-dom">
        <div className="moc-title">Typical Sold DOM (Similar Vehicles)</div>
        
        {typicalDomStats.typicalDom !== null ? (
          <>
            <div className="moc-primary-value">
              {typicalDomStats.typicalDom} days
            </div>
            <div className="moc-typical-dom-spacer"></div>
            {typicalDomStats.marketAvgDom !== null && (
              <div className="moc-footer-text">
                Based on Similar Mileage Vehicles ±15% on last {selectedPeriod === "30D" ? "30" : selectedPeriod === "60D" ? "60" : "90"} days
              </div>
            )}
          </>
        ) : (
          <div className="moc-empty-state">
            Not enough sold data
          </div>
        )}
      </div>

      {/* Card 3: Sold By Dealer Type */}
      <div className="moc-card moc-dealer-type">
        <div className="moc-title">Sold By Dealer Type</div>
        
        {dealerTypeStats.franchisePct !== null ? (
          <>
            {/* Donut Chart */}
            <div className="moc-donut-container">
              <div className="moc-donut-chart">
                <svg viewBox="0 0 100 100" className="moc-donut-svg">
                  {/* Franchise segment */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="rgba(251, 191, 36, 0.9)"
                    strokeWidth="20"
                    strokeDasharray={`${(dealerTypeStats.franchisePct / 100) * 251.3} 251.3`}
                    transform="rotate(-90 50 50)"
                  />
                  {/* Independent segment */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="rgba(96, 165, 250, 0.9)"
                    strokeWidth="20"
                    strokeDasharray={`${(dealerTypeStats.independentPct / 100) * 251.3} 251.3`}
                    strokeDashoffset={-(dealerTypeStats.franchisePct / 100) * 251.3}
                    transform="rotate(-90 50 50)"
                  />
                </svg>
              </div>
            </div>

            {/* Labels and Metrics */}
            <div className="moc-dealer-labels">
              <div className="moc-dealer-item moc-franchise">
                <div className="moc-dealer-percent" style={{ color: 'rgba(251, 191, 36, 0.9)' }}>
                  {dealerTypeStats.franchisePct}% Franchise
                </div>
                {dealerTypeStats.franchiseDom !== null && (
                  <div className="moc-dealer-dom">
                    Avg DOM: {dealerTypeStats.franchiseDom} days
                  </div>
                )}
              </div>
              <div className="moc-dealer-item moc-independent">
                <div className="moc-dealer-percent" style={{ color: 'rgba(96, 165, 250, 0.9)' }}>
                  {dealerTypeStats.independentPct}% Independent
                </div>
                {dealerTypeStats.independentDom !== null && (
                  <div className="moc-dealer-dom">
                    Avg DOM: {dealerTypeStats.independentDom} days
                  </div>
                )}
              </div>
            </div>

            <div className="moc-footer-text">
              Sales mix based on last {selectedPeriod === "30D" ? "30" : selectedPeriod === "60D" ? "60" : "90"} days
            </div>
          </>
        ) : (
          <div className="moc-empty-state">
            No dealer type data available
          </div>
        )}
      </div>

      {/* Card 4: Active By Dealer Type */}
      <div className="moc-card moc-dealer-type moc-active-dealer-type">
        <div className="moc-title">Active By Dealer Type</div>
        
        {activeDealerTypeStats.franchisePct !== null ? (
          <>
            {/* Donut Chart */}
            <div className="moc-donut-container">
              <div className="moc-donut-chart">
                <svg viewBox="0 0 100 100" className="moc-donut-svg">
                  {/* Franchise segment */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="rgba(251, 191, 36, 0.9)"
                    strokeWidth="20"
                    strokeDasharray={`${(activeDealerTypeStats.franchisePct / 100) * 251.3} 251.3`}
                    transform="rotate(-90 50 50)"
                  />
                  {/* Independent segment */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="rgba(96, 165, 250, 0.9)"
                    strokeWidth="20"
                    strokeDasharray={`${(activeDealerTypeStats.independentPct / 100) * 251.3} 251.3`}
                    strokeDashoffset={-(activeDealerTypeStats.franchisePct / 100) * 251.3}
                    transform="rotate(-90 50 50)"
                  />
                </svg>
              </div>
            </div>

            {/* Labels and Metrics */}
            <div className="moc-dealer-labels">
              <div className="moc-dealer-item moc-franchise">
                <div className="moc-dealer-percent" style={{ color: 'rgba(251, 191, 36, 0.9)' }}>
                  {activeDealerTypeStats.franchisePct}% Franchise
                </div>
                <div className="moc-dealer-dom" style={{ visibility: 'hidden' }}>
                  Avg DOM: 0 days
                </div>
              </div>
              <div className="moc-dealer-item moc-independent">
                <div className="moc-dealer-percent" style={{ color: 'rgba(96, 165, 250, 0.9)' }}>
                  {activeDealerTypeStats.independentPct}% Independent
                </div>
                <div className="moc-dealer-dom" style={{ visibility: 'hidden' }}>
                  Avg DOM: 0 days
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="moc-empty-state">
            No dealer type data available
          </div>
        )}
      </div>
    </div>
  );
}

